import { scoreOpportunityCandidate } from './opportunityRadar.js';
import { classifyStructuralCandidate } from './structuralCandidateClassifier.js';

const DEFAULT_MAX_CANDIDATES = 5;
const DEFAULT_STRONG_EVIDENCE_MIN_RATING_COUNT = 1_000;
const DEFAULT_RESEARCH_MIN_RATING_COUNT = 100;
const DEFAULT_RESEARCH_MIN_REVIEW_SAMPLE_COUNT = 10;
const STATUS_TRACK_ID = '6596771144';
const DAILY_TARGET_MARKETS = Object.freeze(['us', 'cn', 'au', 'jp', 'kr']);

const MARKET_NAMES_ZH = Object.freeze({
  us: '美国',
  gb: '英国',
  ca: '加拿大',
  au: '澳大利亚',
  jp: '日本',
  cn: '中国',
  kr: '韩国',
  de: '德国',
  fr: '法国',
  se: '瑞典',
  tw: '台湾',
  hk: '香港',
  it: '意大利',
  es: '西班牙',
  br: '巴西',
  mx: '墨西哥',
  in: '印度',
  ru: '俄罗斯',
  sa: '沙特阿拉伯',
  ae: '阿联酋',
  za: '南非',
});

const GENRE_NAMES_ZH = Object.freeze({
  Utilities: '工具',
  Utility: '工具',
  Travel: '旅行',
  Education: '教育',
  Productivity: '效率',
  Entertainment: '娱乐',
  Sports: '体育',
  Finance: '金融',
  Games: '游戏',
  Photo: '摄影',
  'Photo & Video': '摄影与视频',
  Music: '音乐',
  Lifestyle: '生活',
  Reference: '参考',
  Navigation: '导航',
  Weather: '天气',
  News: '新闻',
  Business: '商务',
  'Social Networking': '社交',
});

const FILTER_STATUS_ZH = Object.freeze({
  needs_review: '需要人工复核',
  downranked: '已降权',
  filtered: '已过滤',
  candidate: '候选',
});

const FILTER_CATEGORY_ZH = Object.freeze({
  low_confidence: '低置信度',
  large_platform: '大平台/内容供给',
});

const FILTER_REASON_ZH = Object.freeze({
  'low-confidence-missing-genre-or-rating': '缓存缺少可靠品类或评分量语境',
  'large-platform-or-media': '大平台、媒体版权或内容供给依赖强',
  'game-or-ip-business': '游戏/IP/发行能力主导',
  'government-or-public-service': '政府或公共服务绑定强',
  'bank-or-finance': '金融监管和信任门槛高',
  'transport-or-ticketing': '交通票务或线下履约绑定强',
  gambling: '博彩或高监管品类',
  'local-content': '强本地内容供给驱动',
});

/**
 * Build a deterministic Chinese daily dogfood report from cached opportunity data.
 * This is pure formatting/scoring logic: no network, no storage writes, no live scan.
 *
 * @param {object[]|{opportunities?: object[], generatedAt?: string|null, report?: object|null}} input
 * @param {{generatedAt?: string|null, minCandidates?: number, maxCandidates?: number, metadataByTrackId?: object|Map<string, object>, reviewsByTrackId?: object|Map<string, object>}} [options]
 * @returns {{status: 'ready'|'insufficient_signal', generatedAt: string|null, reportUrl: string, headline: string, summary: string, counts: object, candidates: object[], noStrongOpportunity: object, statusSample: object|null, methodology: string}}
 */
export function buildDailyOpportunityReport(input, options = {}) {
  const opportunities = Array.isArray(input)
    ? input
    : Array.isArray(input?.opportunities)
      ? input.opportunities
      : Array.isArray(input?.candidates)
        ? input.candidates
        : [];
  const maxCandidates = positiveInteger(options.maxCandidates) ?? DEFAULT_MAX_CANDIDATES;
  const generatedAt = options.generatedAt ?? (Array.isArray(input) ? null : input?.generatedAt ?? null);
  const scored = opportunities
    .map(normalizeOpportunity)
    .filter(isDailySourceMarketAllowed)
    .filter((item) => item.app && item.score !== null)
    .sort(compareOpportunity);
  const audit = dailyAuditFor(opportunities.map(normalizeOpportunity).filter((item) => item.app));
  const reportable = scored.map((item, index) => reportCandidate(item, index + 1, options));
  const selected = selectReportCandidates(reportable, maxCandidates)
    .map((candidate, index) => ({ ...candidate, rank: index + 1 }));
  const statusSample = buildStatusSample(scored);
  const ready = true;
  const counts = categoryCounts(selected);
  const noStrongOpportunity = noStrongOpportunityStatus(selected, ready);

  return {
    status: ready ? 'ready' : 'insufficient_signal',
    generatedAt,
    reportUrl: '/daily-insight/',
    headline: ready
      ? `每日机会报告：${dailyHeadline(counts, noStrongOpportunity)}`
      : '每日机会报告：今天没有有效机会',
    summary: ready
      ? dailySummary({ selected, counts, noStrongOpportunity })
      : `当前缓存中没有满足研究门槛的机会候选；今天不凑数输出。请等待更多榜单历史、评分量变化和评论证据后再复核。`,
    counts,
    candidates: selected,
    audit,
    noStrongOpportunity,
    statusSample,
    methodology: '方法边界：日报主列表只展示满足研究门槛的应用机会；游戏/IP、金融监管、直播版权、VPN、低评论/低评分量/缺少排名历史的信号会被拦截，不会拿来凑数。不估算下载量或收入。',
  };
}

function normalizeOpportunity(item) {
  if (item?.signals) return scoreOpportunityCandidate(item);
  if (item?.trackId !== undefined || item?.name) return normalizeChartCandidate(item);
  return {
    app: item?.app ?? null,
    score: finiteOrNull(item?.score),
    targetScores: Array.isArray(item?.targetScores) ? item.targetScores : [],
    sourceSummary: item?.sourceSummary ?? {},
    topReasons: Array.isArray(item?.topReasons) ? item.topReasons : [],
  };
}

function normalizeChartCandidate(item) {
  const sourceSignal = item?.sourceSignal && typeof item.sourceSignal === 'object' ? item.sourceSignal : {};
  const targetSignals = Array.isArray(item?.targetSignals) ? item.targetSignals : [];
  const score = finiteOrNull(item?.score)
    ?? signalScoreFromChartCandidate(item, targetSignals);
  return {
    ...item,
    app: {
      trackId: item.trackId ?? null,
      name: item.name ?? item.trackName ?? '未命名 App',
      genreId: item.genreId ?? null,
      genreName: item.primaryGenre ?? sourceSignal.primaryGenre ?? null,
      filterStatus: item.filterStatus,
      filterReasons: item.filterReasons,
      filterCategory: item.filterCategory,
    },
    score,
    targetScores: targetSignals.map((signal) => ({
      market: signal.market,
      score: targetScoreFromSignal(signal),
      factors: {
        targetCold: signal.targetCold === true ? 1 : 0,
        localizationGap: signal.missingLocalLanguage === true ? 1 : 0,
        substituteWeakness: null,
      },
      reasons: [
        signal.targetCold === true ? { signal: 'targetCold' } : null,
        signal.missingLocalLanguage === true ? { signal: 'localizationGap' } : null,
      ].filter(Boolean),
    })),
    sourceSummary: {
      chartingMarketCount: 1,
      sourceMarketCount: 1,
      bestRank: item.currentRank ?? null,
      newEntryCount: item.previousRank === null ? 1 : 0,
      risingMarketCount: finiteOrNull(item.rankGain) !== null && item.rankGain > 0 ? 1 : 0,
      maxRatingCount: finiteOrNull(sourceSignal.ratingCount),
    },
    topReasons: [],
  };
}

function signalScoreFromChartCandidate(item, targetSignals) {
  const base = Math.max(0, 101 - (finiteOrNull(item?.currentRank) ?? 101));
  const targetCold = targetSignals.filter((signal) => signal?.targetCold === true).length * 8;
  const languageGap = targetSignals.filter((signal) => signal?.missingLocalLanguage === true).length * 5;
  const ratingSkew = item?.ratingCountSkew?.positiveSignal === true ? 8 : 0;
  const filterPenalty = item?.filterStatus && item.filterStatus !== 'candidate' ? -20 : 0;
  return Math.max(0, base + targetCold + languageGap + ratingSkew + filterPenalty);
}

function targetScoreFromSignal(signal) {
  let score = 0;
  if (signal?.targetCold === true) score += 0.6;
  if (signal?.missingLocalLanguage === true) score += 0.3;
  if (finiteOrNull(signal?.ratingCount) !== null && signal.ratingCount < 20) score += 0.1;
  return Math.min(1, score);
}

function reportCandidate(item, rank, options = {}) {
  const topTargets = rankedTargets(item.targetScores)
    .filter((target) => DAILY_TARGET_MARKETS.includes(normalizeMarket(target.market)))
    .filter(hasTargetEvidence)
    .slice(0, 5);
  const trackId = item.app?.trackId ?? null;
  const metadata = lookupByTrackId(options.metadataByTrackId, trackId);
  const reviewData = lookupByTrackId(options.reviewsByTrackId, trackId);
  const screenshotUrls = screenshotUrlsFor(metadata);
  const userReviewInsights = reviewInsightsFor(reviewData);
  const evidenceQuality = evidenceQualityFor(item, topTargets, {
    ...options,
    hasScreenshots: screenshotUrls.length > 0,
    reviewSampleCount: userReviewInsights.sampleCount ?? 0,
  });
  const rejectReasons = rejectReasonsFor(item);
  const category = categoryFor({ item, topTargets, evidenceQuality, rejectReasons });
  const score = finiteOrNull(item.score);
  const displayScore = score === null ? null : Math.max(0, Math.min(100, Math.round(score)));
  return {
    rank,
    appName: item.app?.name ?? item.app?.trackName ?? '未命名 App',
    trackId,
    sourceMarket: item.market ?? item.app?.market ?? null,
    genreName: genreLabel(metadata?.primaryGenreName ?? item.app?.genreName ?? item.app?.primaryGenreName),
    storeUrl: metadata?.trackViewUrl ?? item.app?.storeUrl ?? null,
    artworkUrl: metadata?.artworkUrl100 ?? metadata?.artworkUrl60 ?? item.app?.artworkUrl100 ?? item.app?.artworkUrl60 ?? null,
    screenshotUrls,
    visualEvidence: visualEvidenceFor(metadata, screenshotUrls),
    appIntro: appIntroFor(item, metadata),
    appSummary: appSummaryFor(item, metadata),
    userReviewInsights,
    score: item.score,
    displayScore,
    scoreLabel: score === null ? '排序分缺失' : '内部排序用，非机会评分',
    targetMarkets: topTargets.map((target) => marketLabel(target.market)),
    sourceSignal: sourceSignalFor(item),
    targetSignals: targetFactsFor(topTargets),
    sourceHeat: sourceHeatText(item.sourceSummary),
    targetCold: targetColdText(topTargets),
    localizationGap: localizationGapText(topTargets),
    substituteWeakness: substituteWeaknessText(topTargets),
    evidenceQuality,
    category,
    rejectReasons,
    scoreBasis: scoreBasisFor(item, topTargets, evidenceQuality, rejectReasons),
    recommendation: recommendationText({ targets: topTargets, category, evidenceQuality, rejectReasons }),
  };
}

function appIntroFor(item, metadata) {
  const appName = item.app?.name ?? item.app?.trackName ?? item.name ?? '';
  const genre = metadata?.primaryGenreName ?? item.app?.genreName ?? item.app?.primaryGenreName ?? '';
  const text = lowerText(appName, genre, metadata?.description);

  if (text.includes('vpn') || text.includes('впн')) {
    return 'VPN / 隐私连接工具，主要卖点是加密、跨区连接或网络访问。';
  }
  if (text.includes('kruger') || text.includes('safari') || text.includes('wildlife')) {
    return '离线旅行 / 野外观察助手，服务特定地点游客记录动物、地图和行程。';
  }
  if (text.includes('prayer') || text.includes('salat') || text.includes('namaz') || text.includes('الصلاة')) {
    return '宗教生活工具，围绕祈祷时间、提醒、方向或日常仪式服务。';
  }
  if (text.includes('live streaming') || text.includes('streaming') || text.includes('sports') || hasWord(text, 'live') || hasWord(text, 'match')) {
    return '直播 / 体育内容平台，价值依赖内容供给、社区或版权资源。';
  }
  if (text.includes('calculator') || text.includes('unit converter')) {
    return '计算器 / 换算工具，解决窄场景里的快速输入和结果确认。';
  }
  if (text.includes('exam') || text.includes('trainer') || text.includes('practice')) {
    return '考试 / 训练工具，围绕明确课程、练习和付费学习场景。';
  }
  if (text.includes('camera') || text.includes('photo') || text.includes('video')) {
    return '拍摄 / 创作者工具，围绕相机、照片或视频工作流。';
  }
  if (genre) return `${genreLabel(genre)}类 app；用途需要结合商店描述和截图复核。`;
  return '用途未能从缓存字段可靠归类，需要补商店描述、截图或人工复核。';
}

function appSummaryFor(item, metadata) {
  const genre = genreLabel(metadata?.primaryGenreName ?? item.app?.genreName ?? item.app?.primaryGenreName);
  const intro = appIntroFor(item, metadata);
  const description = typeof metadata?.description === 'string' ? metadata.description : '';
  const text = lowerText(item.app?.name, item.app?.trackName, item.name, genre, description);
  const features = chineseFeatureSummary(text);
  if (features.length) return `${genre}类。${intro} 商店描述只用于提取功能标签，可归纳为：${features.join('、')}。`;
  return `${genre}类。${intro} 当前没有可用中文化用途归纳，不能只凭名称判断用途；需要补截图、评论和人工复核。`;
}

function reviewInsightsFor(reviewData) {
  if (!reviewData) {
    return {
      status: 'missing',
      summary: '评论样本缺失：还没有请求 Apple customer reviews RSS。',
      sampleCount: 0,
      themes: [],
    };
  }
  if (reviewData.status && reviewData.status !== 'ok') {
    return {
      status: reviewData.status,
      summary: `评论样本缺失：${reviewMissingReason(reviewData.reason ?? reviewData.status)}，不能编造用户评价。`,
      sampleCount: 0,
      themes: [],
    };
  }

  const reviews = normalizeReviews(reviewData.reviews ?? reviewData);
  if (reviews.length === 0) {
    return {
      status: 'empty',
      summary: '公开评论 RSS 没有返回可用评论，不能归纳用户喜欢或抱怨。',
      sampleCount: 0,
      themes: [],
    };
  }

  const ratings = reviews.map((review) => finiteOrNull(review.rating)).filter((rating) => rating !== null);
  const high = ratings.filter((rating) => rating >= 4).length;
  const low = ratings.filter((rating) => rating <= 2).length;
  const themes = reviewThemes(reviews);
  return {
    status: 'ok',
    summary: ratings.length
      ? `公开评论样本 ${reviews.length} 条，均分 ${average(ratings).toFixed(1)}/5；高分 ${high} 条、低分 ${low} 条。`
      : `公开评论样本 ${reviews.length} 条，但 RSS 没有可解析评分。`,
    sampleCount: reviews.length,
    themes,
    snippets: reviewSnippets(reviews),
  };
}

function reviewSnippets(reviews) {
  return reviews
    .filter((review) => review.title || review.content)
    .slice(0, 3)
    .map((review, index) => {
      const rating = finiteOrNull(review.rating) === null ? '' : `${review.rating}/5`;
      const themes = reviewThemes([review]).map((theme) => theme.replace(/（\d+ 条）$/, ''));
      const detail = themes.length ? `提到${themes.join('、').replaceAll('喜欢：', '').replaceAll('抱怨：', '')}` : '没有可自动归类的具体主题，需要人工读原文';
      return [`样本 ${index + 1}`, rating, detail].filter(Boolean).join('：');
    });
}

function chineseFeatureSummary(text) {
  const features = [];
  if (text.includes('offline') || text.includes('map') || text.includes('地图') || text.includes('離線')) features.push('离线地图/离线使用');
  if (text.includes('wildlife') || text.includes('safari') || text.includes('animal')) features.push('野外观察记录');
  if (hasWord(text, 'live') || hasWord(text, 'match') || text.includes('sports') || text.includes('football') || text.includes('basketball')) features.push('赛事结果和赛程');
  if (text.includes('prayer') || text.includes('salat') || text.includes('namaz') || text.includes('الصلاة')) features.push('祈祷时间/提醒');
  if (text.includes('vpn') || text.includes('впн')) features.push('VPN 连接和隐私访问');
  if (text.includes('exam') || text.includes('trainer') || text.includes('practice')) features.push('练习/训练');
  if (text.includes('calculator') || text.includes('converter')) features.push('计算/换算');
  if (text.includes('camera') || text.includes('photo') || text.includes('video')) features.push('拍摄和素材处理');
  return [...new Set(features)].slice(0, 4);
}

function reviewThemes(reviews) {
  const themes = [
    themeHit(reviews, '喜欢：简单/好用', ['easy', 'simple', 'useful', 'helpful', 'great', 'love', '好用', '简单']),
    themeHit(reviews, '喜欢：稳定/流畅', ['fast', 'smooth', 'stable', 'reliable', 'quick', '流畅', '稳定']),
    themeHit(reviews, '喜欢：内容/覆盖', ['content', 'coverage', 'map', 'offline', 'live', 'match', '内容', '地图', '离线']),
    themeHit(reviews, '抱怨：崩溃/错误', ['crash', 'bug', 'broken', 'error', 'freeze', '崩溃', '错误']),
    themeHit(reviews, '抱怨：付费/订阅', ['subscription', 'payment', 'price', 'paid', 'subscribe', '付费', '订阅']),
    themeHit(reviews, '抱怨：内容不可用', ['missing', 'unavailable', 'blocked', 'region', 'not available', '不可用']),
  ].filter(Boolean);
  return themes.slice(0, 4);
}

function reviewMissingReason(reason) {
  const text = String(reason ?? '').trim();
  if (!text) return 'Apple 评论接口没有返回原因';
  if (text === 'no reviews returned') return 'Apple 评论接口没有返回评论';
  if (text === '--no-network') return '生成时关闭了网络请求';
  if (text === 'not_requested') return '尚未请求评论接口';
  return text.replaceAll('_', ' ');
}

function themeHit(reviews, label, words) {
  const count = reviews.filter((review) => {
    const text = lowerText(review.title, review.content);
    return words.some((word) => text.includes(word));
  }).length;
  return count > 0 ? `${label}（${count} 条）` : null;
}

function normalizeReviews(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((review) => ({
      rating: finiteOrNull(review?.rating),
      title: typeof review?.title === 'string' ? review.title.trim() : '',
      content: typeof review?.content === 'string' ? review.content.trim() : '',
    }))
    .filter((review) => review.rating !== null || review.title || review.content);
}

function screenshotUrlsFor(metadata) {
  const urls = [
    ...(Array.isArray(metadata?.screenshotUrls) ? metadata.screenshotUrls : []),
    ...(Array.isArray(metadata?.ipadScreenshotUrls) ? metadata.ipadScreenshotUrls : []),
  ].filter((url) => typeof url === 'string' && url.startsWith('http'));
  return [...new Set(urls)].slice(0, 3);
}

function visualEvidenceFor(metadata, screenshotUrls) {
  if (screenshotUrls.length) {
    const sourceLabel = metadata?.screenshotSource === 'app-store-page' ? 'Apple 商店页面' : 'Apple Lookup';
    return {
      status: 'ok',
      summary: `${sourceLabel}提供 ${screenshotUrls.length} 张商店截图，可作为视觉证据。`,
    };
  }
  if (!metadata) {
    return {
      status: 'missing',
      summary: '未请求 Apple Lookup，缺少商店截图，不能判断产品形态。',
    };
  }
  if (metadata.status && metadata.status !== 'ok') {
    return {
      status: metadata.status,
      summary: `Apple Lookup 未返回可用商店数据（${lookupReasonZh(metadata.reason ?? metadata.status)}），缺少截图，不能判断产品形态。`,
    };
  }
  return {
    status: 'missing',
    summary: 'Apple Lookup 和商店页面都没有拿到截图；该候选缺少视觉证据，必须降级。',
  };
}

function firstSentence(value) {
  const text = typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
  if (!text) return null;
  const match = text.match(/^(.{1,180}?[.!?。！？])(\s|$)/);
  return match ? match[1] : `${text.slice(0, 180)}${text.length > 180 ? '...' : ''}`;
}

function truncate(value, maxLength) {
  const text = typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

function evidenceQualityFor(item, targets, options = {}) {
  const minStrongRatingCount = positiveInteger(options.strongEvidenceMinRatingCount) ?? DEFAULT_STRONG_EVIDENCE_MIN_RATING_COUNT;
  const minResearchRatingCount = positiveInteger(options.researchMinRatingCount) ?? DEFAULT_RESEARCH_MIN_RATING_COUNT;
  const rankVelocity = rankVelocityEvidenceFor(item);
  const ratingGrowth = ratingCountGrowthEvidenceFor(item);
  const ratingCount = finiteOrNull(item.sourceSummary?.maxRatingCount)
    ?? finiteOrNull(item.sourceSummary?.ratingCount)
    ?? maxFinite((item.app?.signals ?? []).map((signal) => signal?.ratingCount));
  const chartingMarketCount = finiteOrNull(item.sourceSummary?.chartingMarketCount);
  const targetEvidenceCount = targets.length;
  const hasTargetGap = targetEvidenceCount > 0;
  const hasScreenshots = options.hasScreenshots === true;
  const reviewSampleCount = finiteOrNull(options.reviewSampleCount) ?? 0;

  if (ratingCount === null) {
    return {
      tier: 'weak',
      sourceRatingCount: null,
      targetEvidenceCount,
      reason: '源市场评分数量缺失，不能作为强机会证据。',
    };
  }
  if (ratingCount < minResearchRatingCount) {
    return {
      tier: 'weak',
      sourceRatingCount: ratingCount,
      targetEvidenceCount,
      reason: `源市场最高评分量只有 ${ratingCount}，低于 ${minResearchRatingCount} 的研究门槛；只能作为弱样本，不能算机会。`,
    };
  }
  if (!rankVelocity.proven) {
    return {
      tier: 'weak',
      sourceRatingCount: ratingCount,
      targetEvidenceCount,
      rankVelocityStatus: rankVelocity.status,
      ratingGrowthStatus: ratingGrowth.status,
      reason: `缺少已确认的排名速度证据（${rankVelocity.reason}）；不能只凭当前榜位判断机会。`,
    };
  }
  if (!hasScreenshots) {
    return {
      tier: 'weak',
      sourceRatingCount: ratingCount,
      targetEvidenceCount,
      rankVelocityStatus: rankVelocity.status,
      ratingGrowthStatus: ratingGrowth.status,
      reason: '缺少商店截图，无法判断产品形态；只能观察，不能作为机会推荐。',
    };
  }
  if (reviewSampleCount < DEFAULT_RESEARCH_MIN_REVIEW_SAMPLE_COUNT) {
    return {
      tier: 'weak',
      sourceRatingCount: ratingCount,
      targetEvidenceCount,
      rankVelocityStatus: rankVelocity.status,
      ratingGrowthStatus: ratingGrowth.status,
      reason: `公开评论样本只有 ${reviewSampleCount} 条，低于 ${DEFAULT_RESEARCH_MIN_REVIEW_SAMPLE_COUNT} 条研究门槛；用户痛点证据不足，不能作为机会推荐。`,
    };
  }
  if (
    ratingCount >= minStrongRatingCount
    && chartingMarketCount !== null
    && chartingMarketCount >= 2
    && hasTargetGap
    && ratingGrowth.status === 'proven'
  ) {
    return {
      tier: 'strong',
      sourceRatingCount: ratingCount,
      targetEvidenceCount,
      rankVelocityStatus: rankVelocity.status,
      ratingGrowthStatus: ratingGrowth.status,
      reason: `源市场评分量达到 ${ratingCount}，至少 ${chartingMarketCount} 个源市场上榜，同时有已记录的评分量增长和覆盖缺口。`,
    };
  }
  return {
    tier: 'medium',
    sourceRatingCount: ratingCount,
    targetEvidenceCount,
    rankVelocityStatus: rankVelocity.status,
    ratingGrowthStatus: ratingGrowth.status,
    reason: hasTargetGap
      ? `有公开热度、覆盖缺口和已确认排名速度，但评分量增长历史是 ${ratingGrowth.status}，未达到强机会门槛。`
      : '有公开热度，但缺少覆盖缺口、本地化缺口或替代品弱度证据。',
  };
}

function isDailySourceMarketAllowed(item) {
  const market = normalizeMarket(item?.market ?? item?.sourceSummary?.sourceMarket ?? item?.sourceSignal?.market ?? item?.app?.market);
  return !market || DAILY_TARGET_MARKETS.includes(market);
}

function dailyAuditFor(items) {
  const raw = Array.isArray(items) ? items : [];
  const sourceMarketCounts = {};
  const blocked = {
    nonFocusSourceMarket: 0,
    gameOrIp: 0,
    lowSourceRatingCount: 0,
    noProvenRankVelocity: 0,
    hardExcluded: 0,
  };

  for (const item of raw) {
    const sourceMarket = normalizeMarket(item?.market ?? item?.sourceSummary?.sourceMarket ?? item?.sourceSignal?.market ?? item?.app?.market);
    if (sourceMarket && DAILY_TARGET_MARKETS.includes(sourceMarket)) {
      sourceMarketCounts[sourceMarket] = (sourceMarketCounts[sourceMarket] ?? 0) + 1;
    }
    if (sourceMarket && !DAILY_TARGET_MARKETS.includes(sourceMarket)) blocked.nonFocusSourceMarket += 1;
    if (structuralClassificationForItem(item).reason === 'game_or_ip_business') blocked.gameOrIp += 1;
    const ratingCount = finiteOrNull(item?.sourceSignal?.ratingCount ?? item?.sourceSummary?.maxRatingCount);
    if (ratingCount === null || ratingCount < DEFAULT_RESEARCH_MIN_RATING_COUNT) blocked.lowSourceRatingCount += 1;
    if (!rankVelocityEvidenceFor(item).proven) blocked.noProvenRankVelocity += 1;
    if ((item?.filterStatus && item.filterStatus !== 'candidate') || rejectReasonsFor(item).some(isHardRejectReason)) {
      blocked.hardExcluded += 1;
    }
  }

  return {
    rawCandidateCount: raw.length,
    focusSourceMarkets: DAILY_TARGET_MARKETS.map(marketLabel),
    sourceMarketCounts,
    blocked,
    note: '这些是原始榜单信号的质检统计，不是机会列表；日报不会展示被拦截的低质量候选。',
  };
}

function rejectReasonsFor(item) {
  const reasons = [];
  const appName = item.app?.name ?? item.app?.trackName ?? item.name ?? '';
  const genreName = item.app?.genreName ?? item.app?.primaryGenreName ?? item.app?.primaryGenre ?? '';
  const filterStatus = item.app?.filterStatus ?? item.filterStatus;
  const filterReasons = Array.isArray(item.app?.filterReasons) ? item.app.filterReasons : item.filterReasons;
  const filterCategory = item.app?.filterCategory ?? item.filterCategory;
  if (filterStatus && filterStatus !== 'candidate') reasons.push(`状态：${FILTER_STATUS_ZH[filterStatus] ?? filterStatus}`);
  if (filterCategory) reasons.push(`降权类别：${FILTER_CATEGORY_ZH[filterCategory] ?? filterCategory}`);
  if (Array.isArray(filterReasons)) {
    for (const reason of filterReasons) {
      if (reason) reasons.push(`原因：${FILTER_REASON_ZH[reason] ?? reason}`);
    }
  }
  if (filterStatus === 'downranked' || filterCategory === 'large_platform') {
    reasons.push('硬排除：平台、媒体、版权或内容供给门槛过高');
  }
  if (isVpnCandidate(appName)) {
    reasons.push('硬排除：VPN/基础设施/合规/获客信任成本过高');
  }
  const structuralHardReject = structuralHardRejectReason(structuralClassificationForItem(item));
  if (structuralHardReject) reasons.push(structuralHardReject);
  return [...new Set(reasons)];
}

export function structuralClassificationForItem(item) {
  const app = item?.app ?? {};
  return classifyStructuralCandidate({
    structuralClassification: item?.structuralClassification ?? app.structuralClassification,
    name: app.name ?? app.trackName ?? item?.name,
    seller: app.seller ?? app.sellerName ?? item?.seller,
    genre: app.genreName ?? app.primaryGenreName ?? app.primaryGenre ?? item?.primaryGenre,
    genreId: app.genreId ?? item?.genreId,
    // 只有存在**真正稳定的英文字段** descriptionEn 时才把它前递给判定；否则不填。
    // 展示文案 description 按取数储面可能被本地化，仍照常传入——但缺稳定英文背书时
    // 分类器会据此显式降级（descriptionDegraded + warning），绝不静默 keep（#319）。
    // 不再把 description 折叠进 descriptionEn：那会让 descriptionEn 恒有值、展示文案恒缺失，
    // 降级判据恒为 false，令降级机制在生产路径整段失效。
    descriptionEn: app.descriptionEn ?? item?.descriptionEn,
    description: app.description ?? item?.description,
  });
}

function structuralHardRejectReason(classification) {
  if (classification?.decision !== 'reject') return null;
  if (classification.reason === 'game_or_ip_business') {
    return '硬排除：游戏/IP/发行能力主导，不适合作为小团队机会';
  }
  if (classification.filterCategory === 'government') {
    return '硬排除：政府、身份或公共服务绑定较强';
  }
  if (classification.filterCategory === 'banking') {
    return '硬排除：金融牌照、资产托管或监管信任门槛过高';
  }
  if (classification.filterCategory === 'local_transit' || classification.filterCategory === 'ticketing') {
    return '硬排除：交通票务或线下履约绑定较强';
  }
  if (classification.filterCategory === 'large_platform') {
    return '硬排除：平台、媒体、版权或内容供给门槛过高';
  }
  return classification.explanation ? `硬排除：${classification.explanation}` : null;
}

function scoreBasisFor(item, targets, evidenceQuality, rejectReasons) {
  const rawScore = finiteOrNull(item.score);
  const basis = [
    rawScore === null
      ? '排序分：缺失；不能用于比较。'
      : `内部排序值：${Math.round(rawScore)}；由榜单热度、排名变化、覆盖缺口、本地化缺口、竞品弱度和降权/剔除项合成，只用于候选排序，不是机会评分。`,
    sourceHeatText(item.sourceSummary),
    sourceVelocityText(item),
    sourceRatingGrowthText(item),
    targetColdText(targets),
    localizationGapText(targets),
    substituteWeaknessText(targets),
    evidenceQuality?.reason,
  ].filter(Boolean);
  if (rejectReasons.length) basis.push(`降权/剔除：${rejectReasons.join('、')}。`);
  return basis;
}

function sourceSignalFor(item) {
  const sourceMarket = normalizeMarket(item.market ?? item.sourceSummary?.sourceMarket ?? item.sourceSignal?.market ?? item.app?.market);
  const currentRank = finiteOrNull(item.currentRank ?? item.sourceSummary?.bestRank);
  const previousRank = finiteOrNull(item.previousRank ?? item.dailyPreviousRank ?? item.slotPreviousRank);
  const storedRankGain = finiteOrNull(item.rankGain ?? item.dailyRankGain ?? item.slotRankGain);
  const rankGain = previousRank !== null && currentRank !== null ? previousRank - currentRank : storedRankGain;
  const sourceRatingCount = finiteOrNull(item.sourceSignal?.ratingCount ?? item.sourceSummary?.maxRatingCount);
  return {
    market: sourceMarket || null,
    marketLabel: sourceMarket ? marketLabel(sourceMarket) : '未知',
    chartType: chartTypeLabel(item.chartType),
    currentRank,
    previousRank,
    rankGain,
    capturedAt: item.capturedAt ?? null,
    baselineDate: item.baselineDate ?? item.dailyBaselineSnapshotId ?? item.slotBaselineSnapshotId ?? null,
    firstSeen: firstSeenText(item),
    velocity: sourceVelocityText(item),
    ratingCount: sourceRatingCount,
    ratingGrowth: sourceRatingGrowthText(item),
  };
}

function targetFactsFor(targets) {
  return targets.map((target) => ({
    market: normalizeMarket(target.market),
    marketLabel: marketLabel(target.market),
    score: Math.round((finiteOrNull(target.score) ?? 0)),
    targetCold: percentText(target.factors?.targetCold),
    localizationGap: target.factors?.localizationGap === 1 ? '有' : '未确认',
    substituteWeakness: finiteOrNull(target.factors?.substituteWeakness) === null
      ? '暂无证据'
      : percentText(target.factors.substituteWeakness),
  }));
}

function sourceVelocityText(item) {
  const velocity = rankVelocityEvidenceFor(item);
  const profile = item.historyProfile?.rankVelocity;
  if (profile?.status) {
    const gain = finiteOrNull(profile.maxRankGain);
    const delta = finiteOrNull(profile.observedRankDelta);
    const gainText = gain === null ? '' : `；最大窗口上升 ${gain}`;
    const deltaText = delta === null ? '' : `；最早观察到当前变化 ${delta >= 0 ? '+' : ''}${delta}`;
    return `上榜速度：${velocity.label}${gainText}${deltaText}。`;
  }
  const currentRank = finiteOrNull(item.currentRank ?? item.sourceSummary?.bestRank);
  const previousRank = finiteOrNull(item.previousRank ?? item.dailyPreviousRank ?? item.slotPreviousRank);
  const storedRankGain = finiteOrNull(item.rankGain ?? item.dailyRankGain ?? item.slotRankGain);
  if (currentRank === null) return '上榜速度：缓存里缺少当前榜位，不能判断。';
  if (previousRank === null) return `上榜速度：本次在源市场新进榜，当前 #${currentRank}；上一基线未进入榜单样本。`;
  const rankGain = previousRank - currentRank;
  const change = rankGain === 0 && storedRankGain !== null ? storedRankGain : rankGain;
  return `上榜速度：从 #${previousRank} 到 #${currentRank}，排名变化 ${change >= 0 ? '+' : ''}${change}。`;
}

function sourceRatingGrowthText(item) {
  const growth = ratingCountGrowthEvidenceFor(item);
  if (growth.status === 'proven' || growth.status === 'flat_or_declining') {
    return `评分量增长：${growth.previousRatingCount} -> ${growth.currentRatingCount}，变化 ${growth.delta >= 0 ? '+' : ''}${growth.delta}。`;
  }
  if (growth.status === 'not_recorded') {
    const currentFromLookup = finiteOrNull(item.sourceSignal?.ratingCount ?? item.sourceSummary?.maxRatingCount);
    if (currentFromLookup !== null) return `评分量增长：当前 Lookup 评分量 ${currentFromLookup}；历史快照没有上一轮评分量，暂不能证明增长。`;
  }
  const current = finiteOrNull(item.sourceSignal?.ratingCount ?? item.sourceSummary?.maxRatingCount);
  const previous = finiteOrNull(item.previousSourceRatingCount ?? item.sourceSummary?.previousMaxRatingCount);
  if (current === null) return '评分量增长：Apple Lookup 没有返回源市场评分量。';
  if (previous === null) return `评分量增长：当前源市场评分量 ${current}；缓存没有上一轮评分量，暂不能判断增长。`;
  const delta = current - previous;
  return `评分量增长：${previous} -> ${current}，变化 ${delta >= 0 ? '+' : ''}${delta}。`;
}

function rankVelocityEvidenceFor(item) {
  const status = typeof item?.historyProfile?.rankVelocity?.status === 'string'
    ? item.historyProfile.rankVelocity.status
    : null;
  if (status === 'proven') {
    return {
      proven: true,
      status,
      label: '已确认排名上升',
      reason: '历史窗口里有明确正向 rank gain',
    };
  }
  if (status === 'new-entry-confirmed') {
    return {
      proven: true,
      status,
      label: '已确认新进榜',
      reason: '完整 top-100 基线未见，当前进入榜单',
    };
  }
  if (status) {
    return {
      proven: false,
      status,
      label: rankVelocityStatusLabel(status),
      reason: item.historyProfile?.rankVelocity?.reason ?? rankVelocityStatusLabel(status),
    };
  }

  const currentRank = finiteOrNull(item?.currentRank ?? item?.sourceSummary?.bestRank);
  const previousRank = finiteOrNull(item?.previousRank ?? item?.dailyPreviousRank ?? item?.slotPreviousRank);
  const storedRankGain = finiteOrNull(item?.rankGain ?? item?.dailyRankGain ?? item?.slotRankGain);
  const baselineStatus = item?.baselineStatus ?? item?.dailyBaselineStatus ?? item?.slotBaselineStatus ?? null;
  if (previousRank !== null && ((currentRank !== null && previousRank > currentRank) || (storedRankGain !== null && storedRankGain > 0))) {
    return {
      proven: true,
      status: 'legacy-rank-gain',
      label: '已确认排名上升',
      reason: '候选字段里有 previousRank/rankGain',
    };
  }
  if (previousRank === null && baselineStatus === 'complete' && currentRank !== null) {
    return {
      proven: true,
      status: 'legacy-new-entry-confirmed',
      label: '已确认新进榜',
      reason: '完整 top-100 基线未见，当前进入榜单',
    };
  }
  return {
    proven: false,
    status: baselineStatus === 'partial' ? 'partial' : baselineStatus === 'insufficient' ? 'insufficient' : 'missing',
    label: '缺少已确认排名速度',
    reason: '没有完整历史窗口证明它是新进榜或持续上升',
  };
}

function rankVelocityStatusLabel(status) {
  if (status === 're-entry') return '历史中曾出现过，是 re-entry，不是真正 first-seen';
  if (status === 'partial') return '只有 partial baseline，不能确认新进榜';
  if (status === 'insufficient') return '缺少可用历史基线';
  if (status === 'unproven') return '历史窗口没有正向排名变化';
  return '缺少已确认排名速度';
}

function ratingCountGrowthEvidenceFor(item) {
  const growth = (item?.ratingCountGrowth && typeof item.ratingCountGrowth === 'object')
    ? item.ratingCountGrowth
    : item?.historyProfile?.ratingCountGrowth;
  if (growth && typeof growth === 'object') {
    return {
      status: growth.status ?? 'unknown',
      previousRatingCount: finiteOrNull(growth.previousRatingCount),
      currentRatingCount: finiteOrNull(growth.currentRatingCount),
      delta: finiteOrNull(growth.delta),
      reason: growth.reason ?? null,
    };
  }
  const current = finiteOrNull(item?.sourceSignal?.ratingCount ?? item?.sourceSummary?.maxRatingCount);
  const previous = finiteOrNull(item?.previousSourceRatingCount ?? item?.sourceSummary?.previousMaxRatingCount);
  if (current === null) {
    return { status: 'not_recorded', previousRatingCount: previous, currentRatingCount: null, delta: null, reason: 'missing current rating count' };
  }
  if (previous === null) {
    return { status: 'not_recorded', previousRatingCount: null, currentRatingCount: current, delta: null, reason: 'missing previous rating count' };
  }
  const delta = current - previous;
  return {
    status: delta > 0 ? 'proven' : 'flat_or_declining',
    previousRatingCount: previous,
    currentRatingCount: current,
    delta,
    reason: delta > 0 ? 'rating count increased' : 'rating count did not increase',
  };
}

function firstSeenText(item) {
  const captured = item.capturedAt ?? null;
  const baseline = item.baselineDate ?? item.dailyBaselineSnapshotId ?? item.slotBaselineSnapshotId ?? null;
  if (captured && baseline) return `${captured} 相对 ${baseline} 基线`;
  if (captured) return `${captured}`;
  return '未知';
}

function categoryFor({ topTargets, evidenceQuality, rejectReasons }) {
  if (rejectReasons.some(isHardRejectReason)) return 'reject';
  if (rejectReasons.length > 0) return 'watch';
  if (evidenceQuality.tier === 'weak') return 'watch';
  if (topTargets.length === 0) return 'watch';
  return 'research';
}

function selectReportCandidates(candidates, maxCandidates) {
  const eligible = candidates.filter((candidate) => (
    candidate.category === 'research'
    && !isLowSampleCandidate(candidate)
    && candidate.evidenceQuality?.tier !== 'weak'
  ));
  const buckets = {
    research: eligible.filter((candidate) => candidate.category === 'research'),
    watch: [],
    reject: [],
  };
  const selected = [];
  const take = (bucket, limit) => {
    for (const candidate of bucket) {
      if (selected.length >= maxCandidates || selected.includes(candidate)) break;
      selected.push(candidate);
      if (selected.filter((item) => item.category === candidate.category).length >= limit) break;
    }
  };

  take(buckets.research, 2);
  for (const candidate of eligible) {
    if (selected.length >= maxCandidates) break;
    if (!selected.includes(candidate)) selected.push(candidate);
  }
  return selected.slice(0, maxCandidates);
}

function isLowSampleCandidate(candidate) {
  const sourceRatingCount = finiteOrNull(candidate?.evidenceQuality?.sourceRatingCount);
  if (sourceRatingCount === null) return candidate?.evidenceQuality?.tier === 'weak';
  return sourceRatingCount < DEFAULT_RESEARCH_MIN_RATING_COUNT;
}

function isHardRejectReason(reason) {
  return String(reason ?? '').startsWith('硬排除');
}

function isVpnCandidate(appName) {
  const text = lowerText(appName);
  return text.includes('vpn') || text.includes('впн');
}

function buildStatusSample(scored) {
  const status = scored.find((item) => String(item.app?.trackId ?? '') === STATUS_TRACK_ID);
  if (!status) return null;

  const targetByMarket = new Map((status.targetScores ?? []).map((target) => [normalizeMarket(target.market), target]));
  const priority = ['jp', 'cn', 'kr', 'au']
    .map((market) => targetByMarket.get(market))
    .filter(Boolean)
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
    .map((target) => `${marketLabel(target.market)} ${target.score ?? '无分'}`);

  return {
    appName: status.app?.name ?? status.app?.trackName ?? 'Status',
    trackId: status.app?.trackId ?? STATUS_TRACK_ID,
    verdict: 'Status 回归样例稳定：英语源市场有强榜单热度，JP/CN/KR 的冷启动和语言缺口更明显；AU 有英语匹配但目标信号仍偏弱，适合作为观察市场而不是最高优先级。',
    targetOrder: priority,
  };
}

function sourceHeatText(sourceSummary = {}) {
  if (finiteOrNull(sourceSummary.bestRank) === null || finiteOrNull(sourceSummary.chartingMarketCount) === null) {
    return '源市场热度：缓存里没有足够榜单信号，不能判断为热。';
  }
  const parts = [
    `${sourceSummary.chartingMarketCount}/${sourceSummary.sourceMarketCount ?? '?'} 个源市场上榜`,
    `最佳榜位 #${sourceSummary.bestRank}`,
  ];
  const newEntryCount = finiteOrNull(sourceSummary.newEntryCount);
  const risingMarketCount = finiteOrNull(sourceSummary.risingMarketCount);
  if (newEntryCount !== null || risingMarketCount !== null) {
    parts.push(`新进 ${newEntryCount ?? 0} 个、上升 ${risingMarketCount ?? 0} 个`);
  }
  return `源市场热度：${parts.join('，')}。`;
}

function targetColdText(targets) {
  if (targets.length === 0) return '覆盖缺口：暂无市场分数。';
  const cold = targets
    .filter((target) => finiteOrNull(target.factors?.targetCold) !== null)
    .map((target) => `${marketLabel(target.market)} ${percentText(target.factors.targetCold)}`);
  return cold.length
    ? `覆盖缺口：${cold.join('、')}，主要体现为市场未上榜、排名弱、评分数量低或不可用。`
    : '覆盖缺口：缓存里缺少市场可用性、榜单或评分数量信号。';
}

function localizationGapText(targets) {
  const gapMarkets = targets
    .filter((target) => target.factors?.localizationGap === 1)
    .map((target) => marketLabel(target.market));
  return gapMarkets.length
    ? `本地化缺口：${gapMarkets.join('、')} 的商店语言列表缺少当地常用语言。`
    : '本地化缺口：当前没有明确语言缺口，或缓存缺少语言列表。';
}

function substituteWeaknessText(targets) {
  const weak = targets
    .filter((target) => finiteOrNull(target.factors?.substituteWeakness) !== null && target.factors.substituteWeakness >= 0.75)
    .map((target) => `${marketLabel(target.market)} ${percentText(target.factors.substituteWeakness)}`);
  return weak.length
    ? `竞品弱度：${weak.join('、')} 的同类榜单样本里强替代品较少。`
    : '竞品弱度：暂无足够证据证明同类替代品偏弱。';
}

function recommendationText({ targets, category, evidenceQuality, rejectReasons }) {
  if (category === 'reject') return `建议：暂不进入机会池；先记录排除原因（${rejectReasons.join('、')}）。`;
  if (targets.length === 0) return '建议：先补齐缓存信号，不做市场判断。';
  if (category === 'watch') return `建议：这只是观察样本，不是机会推荐；先补评分量、榜单连续性、截图和评论证据后再研究 ${targets.map((target) => marketLabel(target.market)).join('、')}。`;
  return `建议：进入研究队列；优先人工复核 ${targets.map((target) => marketLabel(target.market)).join('、')} 的商店页、语言和同类榜单，再决定是否进入选题池。证据等级：${evidenceTierLabel(evidenceQuality.tier)}。`;
}

function categoryCounts(candidates) {
  return candidates.reduce((counts, candidate) => {
    counts[candidate.category] = (counts[candidate.category] ?? 0) + 1;
    return counts;
  }, { reject: 0, watch: 0, research: 0 });
}

function noStrongOpportunityStatus(candidates, ready) {
  const strongResearch = candidates.filter((candidate) => (
    candidate.category === 'research' && candidate.evidenceQuality?.tier === 'strong'
  ));
  if (!ready) {
    return {
      value: true,
      reason: '缓存候选数量不足，不能强行输出机会结论。',
    };
  }
  if (strongResearch.length === 0) {
    return {
      value: true,
      reason: '今天没有同时满足强证据和研究分类的有效机会；“无有效机会”是正常输出。',
    };
  }
  return {
    value: false,
    reason: `有 ${strongResearch.length} 个强证据研究候选，但仍需人工复核版权、监管、获客和本地替代品。`,
  };
}

function dailyHeadline(counts, noStrongOpportunity) {
  if (noStrongOpportunity.value) return '今天没有有效机会，不凑数';
  return `${counts.research} 个有效研究候选`;
}

function dailySummary({ selected, counts, noStrongOpportunity }) {
  const base = selected.length
    ? `今天从缓存 opportunity 数据中选出 ${selected.length} 个有效研究候选。排序只使用 Apple 公开字段和已提交缓存信号，优先看源市场热、已确认排名速度、评分量、评论样本、截图、覆盖缺口、本地化缺口和过滤原因。`
    : '今天没有候选同时满足研究分类、最低评分量、评论样本、视觉证据和非硬排除规则；日报主列表为空，不用低质量数据凑数。';
  return noStrongOpportunity.value ? `${base}${noStrongOpportunity.reason}` : base;
}

function rankedTargets(targetScores = []) {
  return [...targetScores]
    .filter((target) => finiteOrNull(target?.score) !== null)
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1) || normalizeMarket(a.market).localeCompare(normalizeMarket(b.market)));
}

function hasTargetEvidence(target) {
  const factors = target?.factors ?? {};
  const targetCold = finiteOrNull(factors.targetCold);
  const localizationGap = finiteOrNull(factors.localizationGap);
  const substituteWeakness = finiteOrNull(factors.substituteWeakness);
  return (targetCold !== null && targetCold >= 0.5)
    || localizationGap === 1
    || (substituteWeakness !== null && substituteWeakness >= 0.5)
    || (Array.isArray(target?.reasons) && target.reasons.some((reason) => (
      reason?.signal === 'targetCold'
      || reason?.signal === 'localizationGap'
      || reason?.signal === 'substituteWeakness'
    )));
}

function compareOpportunity(a, b) {
  return (b.score ?? -1) - (a.score ?? -1)
    || String(a.app?.name ?? '').localeCompare(String(b.app?.name ?? ''));
}

function marketLabel(market) {
  const code = normalizeMarket(market);
  const name = MARKET_NAMES_ZH[code] ?? code.toUpperCase();
  return `${name}(${code.toUpperCase()})`;
}

function genreLabel(value) {
  const text = typeof value === 'string' && value.trim() ? value.trim() : '未知品类';
  return GENRE_NAMES_ZH[text] ?? text;
}

function hasWord(text, word) {
  return new RegExp(`(^|[^a-z])${word}([^a-z]|$)`, 'i').test(String(text ?? ''));
}

function chartTypeLabel(value) {
  if (value === 'top-free') return '免费榜';
  if (value === 'top-paid') return '付费榜';
  if (value === 'top-grossing') return '畅销榜';
  return value ? String(value) : '未知榜单';
}

function percentText(value) {
  const number = finiteOrNull(value);
  return number === null ? '无信号' : `${Math.round(number * 100)}`;
}

function evidenceTierLabel(value) {
  if (value === 'strong') return '强';
  if (value === 'medium') return '中';
  if (value === 'weak') return '弱';
  return '未知';
}

function lookupReasonZh(reason) {
  const text = String(reason ?? '').trim();
  if (!text) return '未知原因';
  if (text === 'lookup returned no result') return '查询无结果';
  if (text === 'no reviews returned') return '无评论返回';
  if (text === '--no-network') return '生成时关闭网络请求';
  return text.replaceAll('_', ' ');
}

function positiveInteger(value) {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
}

function finiteOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function maxFinite(values) {
  const finite = values.map(finiteOrNull).filter((value) => value !== null);
  return finite.length ? Math.max(...finite) : null;
}

function average(values) {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function lookupByTrackId(value, trackId) {
  if (trackId === null || trackId === undefined) return null;
  const key = String(trackId);
  if (value instanceof Map) return value.get(key) ?? null;
  return value && typeof value === 'object' ? value[key] ?? null : null;
}

function normalizeMarket(value) {
  return String(value ?? '').trim().toLowerCase();
}

function lowerText(...values) {
  return values
    .map((value) => (typeof value === 'string' ? value : ''))
    .join(' ')
    .toLowerCase();
}
